/**
 * Ismétlődő számlák oldal — lista, létrehozás, szerkesztés, generálás, napló
 */
import { useState, useEffect } from 'react';
import { get, post, del, patch } from '../../lib/api';
import { PageHeader, Button, Badge, Card, Input, Modal, EmptyState, NumInput } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import PartnerAutocomplete from '../../components/PartnerAutocomplete';
import MoneyInput from '../../components/ui/MoneyInput';
import ActionsDropdown from '../../components/ActionsDropdown';

interface RecurringInvoice {
  id: string;
  name: string;
  partner_id: string;
  partner_name: string;
  frequency: string;
  frequency_value: number | null;
  start_date: string;
  end_date: string | null;
  next_issue_date: string;
  payment_method: string;
  due_days: number;
  notes: string | null;
  template_lines_json: TemplateLine[];
  is_active: boolean;
  auto_issue: boolean;
  auto_send_email: boolean;
  last_generated_at: string | null;
  generation_count: number;
}

interface TemplateLine {
  description: string;
  quantity: number;
  unit: string;
  unitPriceNet: number;
  vatRate: string;
}

interface LogEntry {
  id: string;
  attempted_at: string;
  for_period_start: string;
  for_period_end: string;
  invoice_id: string | null;
  status: 'success' | 'failed' | 'skipped';
  error_message: string | null;
}

const FREQ_LABELS: Record<string, string> = {
  weekly: 'Heti',
  monthly: 'Havi',
  quarterly: 'Negyedéves',
  yearly: 'Éves',
  custom_days: 'Egyedi',
};

const STATUS_BADGE: Record<string, { label: string; variant: 'success' | 'default' | 'error' }> = {
  success: { label: 'Sikeres', variant: 'success' },
  failed: { label: 'Sikertelen', variant: 'error' },
  skipped: { label: 'Kihagyva', variant: 'default' },
};

function formatDate(iso: string | null | undefined) {
  return iso ? new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
}
function formatPrice(f: string | number) { return Math.round(Number(f)).toLocaleString('hu-HU') + ' Ft'; }

export default function RecurringInvoicesPage() {
  const { toast } = useToast();
  const [items, setItems] = useState<RecurringInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editItem, setEditItem] = useState<RecurringInvoice | null>(null);
  const [showLog, setShowLog] = useState<RecurringInvoice | null>(null);

  async function load() {
    try {
      const data = await get<RecurringInvoice[]>('/recurring-invoices');
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function handleGenerateNow(item: RecurringInvoice) {
    try {
      const result = await post<{ status: string; invoiceId?: string; message?: string }>(`/recurring-invoices/${item.id}/generate-now`, {});
      if (result.status === 'success') {
        toast(`Számla generálva: ${item.name}`, 'success');
      } else if (result.status === 'skipped') {
        toast(result.message || 'Már generálva erre az időszakra.', 'info');
      } else {
        toast(result.message || 'Generálás sikertelen.', 'error');
      }
      load();
    } catch (e: any) {
      toast(e.message || 'Hiba.', 'error');
    }
  }

  async function handleTogglePause(item: RecurringInvoice) {
    try {
      if (item.is_active) {
        await post(`/recurring-invoices/${item.id}/pause`, {});
        toast(`${item.name} szüneteltetve.`, 'info');
      } else {
        await post(`/recurring-invoices/${item.id}/resume`, {});
        toast(`${item.name} újraindítva.`, 'success');
      }
      load();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  async function handleDelete(item: RecurringInvoice) {
    if (!confirm(`Biztosan inaktiválod: "${item.name}"?`)) return;
    try {
      await del(`/recurring-invoices/${item.id}`);
      toast('Ismétlődő számla inaktívvá téve.', 'success');
      load();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  function freqLabel(item: RecurringInvoice) {
    if (item.frequency === 'custom_days') return `${item.frequency_value} naponta`;
    return FREQ_LABELS[item.frequency] || item.frequency;
  }

  function templateTotal(lines: TemplateLine[]) {
    if (!Array.isArray(lines)) return 0;
    return lines.reduce((s, l) => {
      const net = l.quantity * l.unitPriceNet;
      const vat = Math.round(net * Number(l.vatRate || 27) / 100);
      return s + net + vat;
    }, 0);
  }

  return (
    <div>
      <PageHeader title="Ismétlődő számlák" subtitle="Automatikusan generálódó számlák kezelése">
        <Button onClick={() => { setEditItem(null); setShowEditor(true); }}>+ Új ismétlődő számla</Button>
      </PageHeader>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--slate)' }}>Betöltés...</div>
      ) : items.length === 0 ? (
        <EmptyState title="Nincs ismétlődő számla" description="Hozz létre ismétlődő számlát az automatikus számlázáshoz." />
      ) : (
        <div className="space-y-3">
          {items.map(item => {
            const lines = typeof item.template_lines_json === 'string' ? JSON.parse(item.template_lines_json) : item.template_lines_json;
            const gross = templateTotal(lines);
            const isPast = new Date(item.next_issue_date) <= new Date();
            return (
              <Card key={item.id} style={{ padding: '14px 16px', opacity: item.is_active ? 1 : 0.55 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--navy)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{item.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '2px' }}>{item.partner_name}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} onClick={e => e.stopPropagation()}>
                    <Badge variant={item.is_active ? 'success' : 'default'}>{item.is_active ? 'Aktív' : 'Szünetel'}</Badge>
                    <Badge variant="info">{freqLabel(item)}</Badge>
                    <ActionsDropdown actions={[
                      ...(item.is_active ? [{
                        label: 'Számla generálása most', icon: '⚡', onClick: () => handleGenerateNow(item),
                      }] : []),
                      { label: 'Napló', icon: '📋', onClick: () => setShowLog(item) },
                      { label: 'Szerkesztés', icon: '✏', onClick: () => { setEditItem(item); setShowEditor(true); } },
                      { label: item.is_active ? 'Szüneteltetés' : 'Újraindítás', icon: item.is_active ? '⏸' : '▶', onClick: () => handleTogglePause(item) },
                      { separator: true, label: '', onClick: () => {} },
                      { label: 'Inaktiválás', icon: '🗑', onClick: () => handleDelete(item), danger: true },
                    ]} />
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '12px', color: 'var(--slate)' }}>
                  <span>Következő: <strong style={{ color: isPast && item.is_active ? 'var(--red)' : 'var(--navy)' }}>{formatDate(item.next_issue_date)}</strong></span>
                  <span>Kezdés: {formatDate(item.start_date)}</span>
                  {item.end_date && <span>Vég: {formatDate(item.end_date)}</span>}
                  <span>Generálva: {item.generation_count}x</span>
                  {item.auto_issue && <Badge variant="info">Auto kiállítás</Badge>}
                  <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: '14px', color: 'var(--navy)' }}>{formatPrice(gross)}</span>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {showEditor && <RecurringEditorModal
        existing={editItem}
        onClose={() => { setShowEditor(false); setEditItem(null); }}
        onSaved={() => { setShowEditor(false); setEditItem(null); load(); }}
      />}
      {showLog && <GenerationLogModal item={showLog} onClose={() => setShowLog(null)} />}
    </div>
  );
}

// ── Létrehozás / szerkesztés modal ──

interface LineDraft { description: string; quantity: number; unit: string; unitPriceNet: number; vatRate: string; }

function RecurringEditorModal({ existing, onClose, onSaved }: { existing: RecurringInvoice | null; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const isEdit = !!existing;

  const [name, setName] = useState(existing?.name || '');
  const [selectedPartner, setSelectedPartner] = useState<{ id: string; name: string } | null>(
    existing ? { id: existing.partner_id, name: existing.partner_name } : null,
  );
  const [frequency, setFrequency] = useState(existing?.frequency || 'monthly');
  const [frequencyValue, setFrequencyValue] = useState(existing?.frequency_value || 30);
  const [startDate, setStartDate] = useState(existing?.start_date || new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(existing?.end_date || '');
  const [paymentMethod, setPaymentMethod] = useState(existing?.payment_method || 'transfer');
  const [dueDays, setDueDays] = useState(existing?.due_days ?? 30);
  const [notes, setNotes] = useState(existing?.notes || '');
  const [autoIssue, setAutoIssue] = useState(existing?.auto_issue ?? false);
  const [autoSendEmail, setAutoSendEmail] = useState(existing?.auto_send_email ?? false);
  const [lines, setLines] = useState<LineDraft[]>(() => {
    if (existing?.template_lines_json) {
      const tpl = typeof existing.template_lines_json === 'string' ? JSON.parse(existing.template_lines_json) : existing.template_lines_json;
      return tpl.map((l: any) => ({
        description: l.description || '',
        quantity: Number(l.quantity) || 1,
        unit: l.unit || 'db',
        unitPriceNet: Number(l.unitPriceNet) || 0,
        vatRate: l.vatRate || '27',
      }));
    }
    return [{ description: '', quantity: 1, unit: 'db', unitPriceNet: 0, vatRate: '27' }];
  });
  const [saving, setSaving] = useState(false);

  const totals = lines.reduce((s, l) => {
    const net = l.quantity * l.unitPriceNet;
    const vat = Math.round(net * Number(l.vatRate) / 100);
    return { net: s.net + net, vat: s.vat + vat, gross: s.gross + net + vat };
  }, { net: 0, vat: 0, gross: 0 });

  async function handleSave() {
    if (!name.trim()) { toast('Add meg a sablon nevét.', 'error'); return; }
    if (!isEdit && !selectedPartner) { toast('Válassz partnert.', 'error'); return; }
    if (!lines.some(l => l.description.trim())) { toast('Adj hozzá legalább egy tételt.', 'error'); return; }

    setSaving(true);
    try {
      if (isEdit) {
        await patch(`/recurring-invoices/${existing!.id}`, {
          name: name.trim(),
          endDate: endDate || null,
          dueDays,
          notes: notes || null,
          autoIssue,
          autoSendEmail,
          templateLines: lines.filter(l => l.description.trim()).map(l => ({
            description: l.description.trim(),
            quantity: l.quantity,
            unit: l.unit,
            unitPriceNet: l.unitPriceNet,
            vatRate: l.vatRate,
          })),
        });
        toast('Ismétlődő számla módosítva.', 'success');
      } else {
        await post('/recurring-invoices', {
          name: name.trim(),
          partnerId: selectedPartner!.id,
          frequency,
          frequencyValue: frequency === 'custom_days' ? frequencyValue : undefined,
          startDate,
          endDate: endDate || null,
          paymentMethod,
          dueDays,
          notes: notes || null,
          autoIssue,
          autoSendEmail,
          templateLines: lines.filter(l => l.description.trim()).map(l => ({
            description: l.description.trim(),
            quantity: l.quantity,
            unit: l.unit,
            unitPriceNet: l.unitPriceNet,
            vatRate: l.vatRate,
          })),
        });
        toast('Ismétlődő számla létrehozva.', 'success');
      }
      onSaved();
    } catch (e: any) {
      toast(e.message || 'Mentés sikertelen.', 'error');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={true} onClose={onClose} title={isEdit ? 'Ismétlődő számla szerkesztése' : '+ Új ismétlődő számla'}>
      <div className="space-y-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Input label="Sablon neve *" value={name} onChange={e => setName(e.target.value)} placeholder="Pl. Havi karbantartás — PartnerNév" />

        {!isEdit && (
          <PartnerAutocomplete label="Partner *" value={selectedPartner} onChange={setSelectedPartner} required placeholder="Keress partnert..." />
        )}
        {isEdit && (
          <div>
            <label className="form-label">Partner</label>
            <div className="form-input" style={{ background: '#f9fafb', color: 'var(--slate)' }}>{existing!.partner_name}</div>
          </div>
        )}

        {!isEdit && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: frequency === 'custom_days' ? '1fr 1fr' : '1fr', gap: '12px' }}>
              <div>
                <label className="form-label">Gyakoriság *</label>
                <select value={frequency} onChange={e => setFrequency(e.target.value)} className="form-input">
                  <option value="weekly">Heti</option>
                  <option value="monthly">Havi</option>
                  <option value="quarterly">Negyedéves</option>
                  <option value="yearly">Éves</option>
                  <option value="custom_days">Egyedi (napokban)</option>
                </select>
              </div>
              {frequency === 'custom_days' && (
                <Input label="Napok száma *" type="number" value={String(frequencyValue)} onChange={e => setFrequencyValue(Number(e.target.value))} />
              )}
            </div>
            <Input label="Kezdés dátuma *" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
          <Input label="Befejezés dátuma" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          {!isEdit && (
            <div>
              <label className="form-label">Fizetési mód</label>
              <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className="form-input">
                <option value="transfer">Átutalás</option>
                <option value="cash">Készpénz</option>
                <option value="card">Bankkártya</option>
              </select>
            </div>
          )}
          <Input label="Fizetési határidő (nap)" type="number" value={String(dueDays)} onChange={e => setDueDays(Number(e.target.value))} />
        </div>

        {/* Tételek */}
        <div>
          <label className="form-label">Számla tételek *</label>
          <div style={{ fontSize: '11px', color: 'var(--slate)', marginBottom: '6px' }}>
            Használhatsz placeholdereket: {'{month_name}'}, {'{year}'}, {'{period_start}'}, {'{period_end}'}
          </div>
          {lines.map((l, i) => (
            <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'flex-end' }}>
              <input value={l.description} onChange={e => { const n = [...lines]; n[i] = { ...n[i], description: e.target.value }; setLines(n); }}
                placeholder="Pl. Karbantartás — {month_name} {year}" className="form-input" style={{ flex: 2 }} />
              <NumInput value={l.quantity} onChange={v => { const n = [...lines]; n[i] = { ...n[i], quantity: v }; setLines(n); }}
                min={0.001} className="form-input" style={{ width: '60px', textAlign: 'center' }} />
              <input value={l.unit} onChange={e => { const n = [...lines]; n[i] = { ...n[i], unit: e.target.value }; setLines(n); }}
                className="form-input" style={{ width: '50px', textAlign: 'center' }} title="Egység" />
              <MoneyInput value={l.unitPriceNet} onChange={v => { const n = [...lines]; n[i] = { ...n[i], unitPriceNet: v }; setLines(n); }} placeholder="Nettó egységár" />
              <select value={l.vatRate} onChange={e => { const n = [...lines]; n[i] = { ...n[i], vatRate: e.target.value }; setLines(n); }}
                className="form-input" style={{ width: '65px' }}>
                <option value="27">27%</option><option value="18">18%</option><option value="5">5%</option><option value="0">0%</option>
              </select>
              <button onClick={() => setLines(lines.filter((_, j) => j !== i))}
                style={{ color: 'var(--red)', border: 'none', background: 'none', cursor: 'pointer', fontSize: '16px' }}>x</button>
            </div>
          ))}
          <button onClick={() => setLines(prev => [...prev, { description: '', quantity: 1, unit: 'db', unitPriceNet: 0, vatRate: '27' }])}
            style={{ fontSize: '13px', color: 'var(--orange)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>+ Új tétel</button>
        </div>

        {/* Összesítő */}
        <div style={{ background: 'var(--mist)', borderRadius: 'var(--r)', padding: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)' }}><span>Nettó</span><span>{formatPrice(totals.net)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--slate)' }}><span>ÁFA</span><span>{formatPrice(totals.vat)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: 700, color: 'var(--navy)', borderTop: '1px solid var(--line)', paddingTop: '6px', marginTop: '4px' }}><span>Bruttó</span><span>{formatPrice(totals.gross)}</span></div>
        </div>

        {/* Megjegyzés */}
        <div>
          <label className="form-label">Megjegyzés (megjelenik a számlán)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="form-input" placeholder="Pl. Havi díj — {month_name} {year}" />
        </div>

        {/* Automatikus beállítások */}
        <div style={{ background: 'var(--mist)', borderRadius: 'var(--r)', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', color: 'var(--navy)' }}>
            <input type="checkbox" checked={autoIssue} onChange={e => setAutoIssue(e.target.checked)} style={{ width: '16px', height: '16px' }} />
            <span><strong>Automatikus kiállítás</strong> — A számla azonnal kiállítva lesz (nem vázlat)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', color: 'var(--slate)' }}>
            <input type="checkbox" checked={autoSendEmail} onChange={e => setAutoSendEmail(e.target.checked)} style={{ width: '16px', height: '16px' }} />
            <span><strong>Automatikus e-mail küldés</strong> — A számla PDF-je automatikusan elküldésre kerül a partner e-mail címére</span>
          </label>
        </div>

      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', paddingTop: '12px', borderTop: '1px solid var(--line)', marginTop: '12px' }}>
        <Button variant="ghost" onClick={onClose}>Mégse</Button>
        <Button onClick={handleSave} loading={saving}>{isEdit ? 'Mentés' : 'Létrehozás'}</Button>
      </div>
    </Modal>
  );
}

// ── Generálási napló modal ──

function GenerationLogModal({ item, onClose }: { item: RecurringInvoice; onClose: () => void }) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await get<LogEntry[]>(`/recurring-invoices/${item.id}/log`);
        setLogs(Array.isArray(data) ? data : []);
      } catch { setLogs([]); }
      setLoading(false);
    })();
  }, [item.id]);

  return (
    <Modal isOpen={true} onClose={onClose} title={`Napló — ${item.name}`}>
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--slate)' }}>Betöltés...</div>
        ) : logs.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--slate)' }}>Még nem volt generálás.</div>
        ) : (
          <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {logs.map((log, i) => {
              const st = STATUS_BADGE[log.status] || { label: log.status, variant: 'default' as const };
              return (
                <div key={log.id} style={{ display: 'flex', gap: '10px', padding: '10px 12px', borderBottom: i < logs.length - 1 ? '1px solid var(--line-2)' : undefined, alignItems: 'center', fontSize: '13px' }}>
                  <Badge variant={st.variant}>{st.label}</Badge>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'var(--navy)' }}>{formatDate(log.for_period_start)} — {formatDate(log.for_period_end)}</div>
                    <div style={{ fontSize: '11px', color: 'var(--slate)' }}>{new Date(log.attempted_at).toLocaleString('hu-HU')}</div>
                    {log.error_message && <div style={{ fontSize: '11px', color: 'var(--red)', marginTop: '2px' }}>{log.error_message}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '12px' }}>
        <Button variant="ghost" onClick={onClose}>Bezárás</Button>
      </div>
    </Modal>
  );
}